import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Clock, AlertTriangle, CheckCircle, XCircle, ChevronRight,
  Maximize, Shield, Send, Loader2, Monitor, Ban
} from "lucide-react";
import { Button } from "../components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_INCIDENTS = 2; // 2nd incident = auto-close

export default function ExamenPublicoPage() {
  const { token } = useParams();
  const [examen, setExamen] = useState(null);
  const [error, setError] = useState(null);
  const [phase, setPhase] = useState("loading"); // loading, rules, started, result
  const [accepted, setAccepted] = useState(false);
  const [preguntas, setPreguntas] = useState([]);
  const [respuestas, setRespuestas] = useState({});
  const [currentQ, setCurrentQ] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [incidencias, setIncidencias] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [warningVisible, setWarningVisible] = useState(false);
  const [multiMonitor, setMultiMonitor] = useState(false);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);
  const incidenciasRef = useRef([]);
  const autoClosedRef = useRef(false);

  // Fetch exam info (not questions yet)
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/examenes/publico/${token}`);
        setExamen(res.data);
        setPhase("rules");
      } catch (err) {
        setError(err.response?.data?.detail || "Error al cargar el examen");
      }
    })();
  }, [token]);

  // Multi-monitor detection
  useEffect(() => {
    if (phase !== "rules") return;
    const checkScreens = async () => {
      try {
        if (window.screen.isExtended !== undefined) {
          setMultiMonitor(window.screen.isExtended);
        } else if (window.screenLeft !== undefined && window.screenTop !== undefined) {
          // Heuristic: if screen width is much larger than window available width
          const ratio = window.screen.width / window.screen.availWidth;
          setMultiMonitor(ratio > 1.5);
        }
      } catch (e) { /* ignore */ }
    };
    checkScreens();
    const screenChange = () => checkScreens();
    if (window.screen?.addEventListener) {
      window.screen.addEventListener("change", screenChange);
    }
    return () => {
      if (window.screen?.removeEventListener) {
        window.screen.removeEventListener("change", screenChange);
      }
    };
  }, [phase]);

  // Timer
  useEffect(() => {
    if (phase !== "started" || resultado) return;
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [phase, resultado]);

  // ============ ANTI-CHEAT SYSTEM ============
  const registerIncident = useCallback((tipo, detalle) => {
    if (autoClosedRef.current) return;
    const inc = { tipo, detalle, timestamp: new Date().toISOString() };
    const updated = [...incidenciasRef.current, inc];
    incidenciasRef.current = updated;
    setIncidencias(updated);
    axios.post(`${API}/api/examenes/publico/${token}/incidencia`, inc).catch(() => {});

    if (updated.length === 1) {
      // 1st incident: warning
      setWarningVisible(true);
    } else if (updated.length >= MAX_INCIDENTS) {
      // 2nd incident: auto-close
      autoClosedRef.current = true;
      handleSubmit();
    }
  }, [token]);

  // Tab change + blur + fullscreen exit detection
  useEffect(() => {
    if (phase !== "started" || resultado) return;

    const handleVisibility = () => {
      if (document.hidden) {
        registerIncident("cambio_pestana", "El usuario cambió de pestaña o minimizó el navegador");
      }
    };

    const handleBlur = () => {
      registerIncident("perdida_foco", "La ventana del navegador perdió el foco (Alt+Tab, clic fuera, etc.)");
    };

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("blur", handleBlur);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("blur", handleBlur);
    };
  }, [phase, resultado, registerIncident]);

  // Fullscreen exit detection
  useEffect(() => {
    if (phase !== "started" || resultado) return;
    const handleFsChange = () => {
      const fs = !!document.fullscreenElement;
      setIsFullscreen(fs);
      if (!fs && examen?.requiere_pantalla_completa) {
        registerIncident("salida_pantalla_completa", "El usuario salió de pantalla completa");
      }
    };
    document.addEventListener("fullscreenchange", handleFsChange);
    return () => document.removeEventListener("fullscreenchange", handleFsChange);
  }, [phase, resultado, examen, registerIncident]);

  // Prevent copy/paste/right-click
  useEffect(() => {
    if (phase !== "started" || resultado) return;
    const prevent = (e) => e.preventDefault();
    const preventCtrl = (e) => {
      if (e.ctrlKey && (e.key === "c" || e.key === "v" || e.key === "x" || e.key === "a")) e.preventDefault();
    };
    document.addEventListener("copy", prevent);
    document.addEventListener("paste", prevent);
    document.addEventListener("cut", prevent);
    document.addEventListener("keydown", preventCtrl);
    return () => {
      document.removeEventListener("copy", prevent);
      document.removeEventListener("paste", prevent);
      document.removeEventListener("cut", prevent);
      document.removeEventListener("keydown", preventCtrl);
    };
  }, [phase, resultado]);

  const enterFullscreen = () => {
    const el = document.documentElement;
    if (el.requestFullscreen) el.requestFullscreen();
    else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
    setIsFullscreen(true);
  };

  // ============ START EXAM ============
  const handleStart = async () => {
    try {
      const res = await axios.post(`${API}/api/examenes/publico/${token}/iniciar`);
      setPreguntas(res.data.preguntas || []);
      setTimeLeft((res.data.tiempo_limite_minutos || 30) * 60);
      if (examen?.requiere_pantalla_completa) enterFullscreen();
      setPhase("started");
      startTimeRef.current = Date.now();
    } catch (err) {
      setError(err.response?.data?.detail || "Error al iniciar el examen");
    }
  };

  // ============ SUBMIT ============
  const handleSubmit = useCallback(async () => {
    if (submitting || resultado) return;
    setSubmitting(true);
    if (timerRef.current) clearInterval(timerRef.current);
    const tiempoUsado = startTimeRef.current ? Math.round((Date.now() - startTimeRef.current) / 1000) : 0;
    try {
      const res = await axios.post(`${API}/api/examenes/publico/${token}/responder`, {
        respuestas,
        tiempo_usado_segundos: tiempoUsado,
        incidencias: incidenciasRef.current
      });
      setResultado(res.data);
      setPhase("result");
      if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    } catch (err) {
      const detail = err.response?.data?.detail || "Error al enviar respuestas";
      if (detail.includes("completado")) {
        setResultado({ cerrado_por_sistema: true });
        setPhase("result");
      } else {
        alert(detail);
      }
    }
    setSubmitting(false);
  }, [submitting, resultado, respuestas, token]);

  const formatTime = (s) => `${Math.floor(s / 60).toString().padStart(2, "0")}:${(s % 60).toString().padStart(2, "0")}`;

  // ============ ERROR SCREEN ============
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4" data-testid="examen-error">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <Ban className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">No se puede acceder</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  // ============ LOADING ============
  if (phase === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  // ============ RESULT SCREEN ============
  if (phase === "result" && resultado) {
    const wasClosed = autoClosedRef.current || resultado.cerrado_por_sistema;
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4" data-testid="examen-resultado">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-lg text-center">
          {wasClosed && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
              <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-2" />
              <p className="text-red-700 font-medium">Examen cerrado por incidencias de seguridad</p>
              <p className="text-red-600 text-sm mt-1">Se detectaron múltiples violaciones de las reglas del examen. Las respuestas registradas hasta el momento fueron enviadas.</p>
            </div>
          )}
          {resultado.mostrar_resultados ? (
            <>
              {resultado.aprobado ? (
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              ) : (
                <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              )}
              <h2 className="text-2xl font-bold mb-2">{resultado.aprobado ? "Aprobado" : "No Aprobado"}</h2>
              <p className="text-5xl font-bold mb-4" style={{ color: resultado.aprobado ? "#16a34a" : "#dc2626" }}>{resultado.calificacion}%</p>
              <p className="text-gray-500">{resultado.puntos_obtenidos} de {resultado.total_puntos} puntos</p>
              {incidencias.length > 0 && (
                <p className="text-amber-600 text-sm mt-4 flex items-center justify-center gap-1"><AlertTriangle className="w-4 h-4" />{incidencias.length} incidencia(s) registrada(s)</p>
              )}
            </>
          ) : (
            <>
              <CheckCircle className="w-16 h-16 text-blue-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2">Examen Completado</h2>
              <p className="text-gray-500">Sus respuestas han sido registradas. Los resultados serán compartidos por el administrador.</p>
            </>
          )}
          <p className="text-sm text-gray-400 mt-6">Puede cerrar esta pestaña.</p>
        </div>
      </div>
    );
  }

  // ============ RULES & CONFIRMATION SCREEN ============
  if (phase === "rules" && examen) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4" data-testid="examen-inicio">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-lg w-full">
          <div className="text-center mb-6">
            <Shield className="w-12 h-12 text-indigo-600 mx-auto mb-3" />
            <h2 className="text-2xl font-bold text-gray-900">{examen.titulo}</h2>
            {examen.descripcion && <p className="text-gray-500 mt-2">{examen.descripcion}</p>}
          </div>

          <div className="bg-gray-50 rounded-lg p-4 mb-6 space-y-2">
            <p className="text-sm"><strong>Candidato:</strong> {examen.nombre_candidato}</p>
            <p className="text-sm"><strong>Tiempo límite:</strong> {examen.tiempo_limite_minutos} minutos</p>
            <p className="text-sm"><strong>Preguntas:</strong> {examen.total_preguntas || "N/A"}</p>
          </div>

          {/* Multi-monitor warning */}
          {multiMonitor && (
            <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4 mb-4" data-testid="multi-monitor-warning">
              <div className="flex items-center gap-2 text-red-700 font-medium mb-1">
                <Monitor className="w-5 h-5" />Se detectaron múltiples monitores
              </div>
              <p className="text-sm text-red-600">Debe desconectar los monitores adicionales y usar únicamente una pantalla antes de iniciar el examen. Esto es un requisito obligatorio.</p>
            </div>
          )}

          <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4 mb-6" data-testid="reglas-examen">
            <h4 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />Reglas del Examen - Lea cuidadosamente
            </h4>
            <ul className="text-sm text-red-700 space-y-2">
              <li className="flex items-start gap-2">
                <Ban className="w-4 h-4 mt-0.5 shrink-0" />
                <span><strong>Una sola oportunidad:</strong> Una vez que inicie el examen, no podrá volver atrás ni ingresar nuevamente.</span>
              </li>
              <li className="flex items-start gap-2">
                <Ban className="w-4 h-4 mt-0.5 shrink-0" />
                <span><strong>Sin navegación hacia atrás:</strong> No podrá regresar a preguntas anteriores una vez avance.</span>
              </li>
              {examen.requiere_pantalla_completa && (
                <li className="flex items-start gap-2">
                  <Maximize className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>Se requiere pantalla completa durante todo el examen.</span>
                </li>
              )}
              {examen.detectar_cambio_pestana && (
                <>
                  <li className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span><strong>Detección de trampas:</strong> El sistema detecta cambio de pestaña, cambio de ventana, minimizar el navegador o cualquier pérdida de foco.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span><strong>1ra incidencia:</strong> Recibirá un llamado de atención.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <XCircle className="w-4 h-4 mt-0.5 shrink-0 text-red-600" />
                    <span><strong>2da incidencia:</strong> El examen se cerrará automáticamente con las respuestas registradas hasta ese momento.</span>
                  </li>
                </>
              )}
              <li className="flex items-start gap-2">
                <Clock className="w-4 h-4 mt-0.5 shrink-0" />
                <span>El examen se envía automáticamente al terminar el tiempo.</span>
              </li>
              <li className="flex items-start gap-2">
                <Ban className="w-4 h-4 mt-0.5 shrink-0" />
                <span>No está permitido copiar, cortar o pegar texto.</span>
              </li>
            </ul>
          </div>

          <label className="flex items-start gap-3 mb-6 p-3 border-2 rounded-lg cursor-pointer transition-all select-none
            ${accepted ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'}"
            data-testid="checkbox-aceptar-reglas">
            <input type="checkbox" checked={accepted} onChange={e => setAccepted(e.target.checked)} className="w-5 h-5 mt-0.5 accent-green-600" />
            <span className="text-sm font-medium text-gray-800">He leído y acepto las reglas del examen. Entiendo que una vez iniciado no podré volver atrás ni ingresar nuevamente.</span>
          </label>

          <Button onClick={handleStart} className="w-full" size="lg"
            disabled={!accepted || multiMonitor}
            data-testid="btn-iniciar-examen">
            {multiMonitor ? (
              <><Monitor className="w-4 h-4 mr-2" />Desconecte monitores adicionales</>
            ) : (
              <><Maximize className="w-4 h-4 mr-2" />Iniciar Examen</>
            )}
          </Button>
          {multiMonitor && <p className="text-xs text-red-500 text-center mt-2">No puede iniciar con múltiples monitores conectados.</p>}
        </div>
      </div>
    );
  }

  // ============ EXAM IN PROGRESS ============
  const preguntaActual = preguntas[currentQ];
  const answered = Object.keys(respuestas).length;

  return (
    <div className="min-h-screen bg-gray-100 select-none" data-testid="examen-en-progreso" onContextMenu={e => e.preventDefault()}>
      {/* Warning Overlay */}
      {warningVisible && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="warning-overlay">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md text-center animate-pulse">
            <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-amber-800 mb-2">Llamado de Atención</h3>
            <p className="text-gray-700 mb-2">Se ha detectado que abandonó la ventana del examen.</p>
            <p className="text-red-600 font-semibold mb-4">Si esto vuelve a ocurrir, el examen se cerrará automáticamente.</p>
            <Button onClick={() => { setWarningVisible(false); enterFullscreen(); }} className="bg-amber-500 hover:bg-amber-600" data-testid="btn-aceptar-warning">
              Entendido, continuar examen
            </Button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-indigo-900 text-white px-4 py-3 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <Shield className="w-5 h-5" />
          <span className="font-medium text-sm hidden sm:inline">{examen?.titulo}</span>
        </div>
        <div className="flex items-center gap-4">
          {!isFullscreen && examen?.requiere_pantalla_completa && (
            <button onClick={enterFullscreen} className="text-amber-300 text-xs flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5" />Volver a pantalla completa
            </button>
          )}
          {incidencias.length > 0 && (
            <span className="text-amber-300 text-xs flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5" />{incidencias.length}/{MAX_INCIDENTS}
            </span>
          )}
          <div className={`font-mono text-lg font-bold ${timeLeft < 60 ? "text-red-400 animate-pulse" : ""}`}>
            <Clock className="w-4 h-4 inline mr-1" />{formatTime(timeLeft)}
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-4 md:p-6">
        <div className="flex items-center justify-between text-sm text-gray-500 mb-2">
          <span>Pregunta {currentQ + 1} de {preguntas.length}</span>
          <span>{answered} respondidas</span>
        </div>
        <div className="w-full bg-gray-300 rounded-full h-1.5 mb-6">
          <div className="bg-indigo-600 h-1.5 rounded-full transition-all" style={{ width: `${(currentQ + 1) / preguntas.length * 100}%` }} />
        </div>

        {preguntaActual && (
          <div className="bg-white rounded-xl shadow p-6 mb-6" data-testid={`pregunta-${currentQ}`}>
            <p className="text-lg font-medium text-gray-900 mb-4">{preguntaActual.texto}</p>

            {(preguntaActual.tipo === "opcion_multiple" || preguntaActual.tipo === "verdadero_falso") && (
              <div className="space-y-2">
                {preguntaActual.opciones?.map((op, i) => (
                  <button key={i} data-testid={`opcion-${i}`}
                    className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all ${
                      respuestas[preguntaActual.id] === op ? "border-indigo-600 bg-indigo-50 text-indigo-900" : "border-gray-200 hover:border-gray-300 text-gray-700"
                    }`}
                    onClick={() => setRespuestas(prev => ({...prev, [preguntaActual.id]: op}))}>
                    <span className="font-medium mr-2">{String.fromCharCode(65 + i)}.</span>{op}
                  </button>
                ))}
              </div>
            )}

            {preguntaActual.tipo === "respuesta_corta" && (
              <textarea data-testid="input-respuesta-corta"
                className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:border-indigo-600 focus:outline-none"
                rows={3} value={respuestas[preguntaActual.id] || ""}
                onChange={e => setRespuestas(prev => ({...prev, [preguntaActual.id]: e.target.value}))}
                placeholder="Escriba su respuesta aquí..." />
            )}
          </div>
        )}

        {/* Navigation - NO back button */}
        <div className="flex items-center justify-between">
          <div className="text-xs text-gray-400">No es posible volver a preguntas anteriores</div>

          {currentQ < preguntas.length - 1 ? (
            <Button onClick={() => setCurrentQ(prev => Math.min(preguntas.length - 1, prev + 1))} data-testid="btn-siguiente">
              Siguiente<ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={() => {
              if (answered < preguntas.length && !window.confirm(`Solo ha respondido ${answered} de ${preguntas.length} preguntas. ¿Desea enviar de todos modos?`)) return;
              handleSubmit();
            }} disabled={submitting} data-testid="btn-enviar-respuestas" className="bg-green-600 hover:bg-green-700">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}Enviar Examen
            </Button>
          )}
        </div>

        {/* Question indicators (current and future only) */}
        <div className="flex gap-1 flex-wrap justify-center mt-6">
          {preguntas.map((p, i) => (
            <div key={i}
              className={`w-7 h-7 rounded-full text-xs font-medium flex items-center justify-center ${
                i === currentQ ? "bg-indigo-600 text-white" :
                i < currentQ ? (respuestas[p.id] ? "bg-green-200 text-green-800" : "bg-gray-300 text-gray-500") :
                "bg-gray-200 text-gray-500"
              }`}
            >{i + 1}</div>
          ))}
        </div>
      </div>
    </div>
  );
}
